// TestQuiz (patched, normalized editor/backend types)
import React, { useState, useEffect, useRef } from "react";
import styles from "./TestQuiz.module.css";
import {
  useHistory,
  useLocation,
  useParams,
} from "react-router-dom/cjs/react-router-dom.min";
import MultipleChoiceQuestion from "../QuizType/MultipleChoiceQuestion";
import QuestionTypeModal from "../Modal/QuestionTypeModal";
import CheckboxesQuestion from "../QuizType/CheckboxesQuestion";
import TrueFalseQuestion from "../QuizType/TrueFalseQuestion";
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import AddIcon from "@mui/icons-material/Add";
import FillInTheBlankQuestion from "../QuizType/FillInTheBlankQuestion";
import GridQuestionComponent from "../QuizType/GridQuestion";
import ImageQuestionComponent from "../QuizType/ImageQuestion";
import { useDispatch } from "react-redux";
import { createTemplate } from "../../../../../redux/actions/courses";
import { handleErrorMessage } from "../../../../../redux/actions/classes";
import { isValid } from "date-fns";
import { useTranslation } from "react-i18next";
import { CircularProgress } from "@mui/material";
import Button from "../../../../../components/assignmentAndQuiz/Button/Button";
import QuizIcon from "@mui/icons-material/Quiz";

const TestQuiz = () => {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const { id } = useParams();
  const { t } = useTranslation();

  const [quizData, setQuizData] = useState({
    title: "",
    instructions: "",
    questions: [],
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentQuestionType, setCurrentQuestionType] = useState(null);
  const [isEditingQuestion, setIsEditingQuestion] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);

  const [newQuestionData, setNewQuestionData] = useState(null);
  const [activeQuestions, setActiveQuestions] = useState([]);
  const [loading, setLoading] = useState(false);

  const refs = useRef([]);

  // --- Helper: map incoming `type` values to internal editor types ---
  const typeMap = {
    mcq: "multipleChoice",
    multipleChoice: "multipleChoice",
    checkboxes: "checkboxes",
    fillBlanks: "fillBlanks",
    "fillblanks": "fillBlanks",
    fillInBlanks: "fillBlanks",
    trueFalse: "trueFalse",
    truefalse: "trueFalse",
    grid: "grid",
    imageChoice: "imageChoice",
    imagechoice: "imageChoice",
  };

  // small helper: normalize any incoming type to editor type
  const toEditorType = (tpe) => (tpe ? typeMap[tpe] || tpe : "multipleChoice");

  // Map internal/editor type -> backend type (centralized)
  const backendTypeMap = {
    multipleChoice: "mcq",
    checkboxes: "checkboxes",
    fillBlanks: "fillInBlanks",
    trueFalse: "trueFalse",
    grid: "grid",
    imageChoice: "imageChoice",
  };

  // Normalize location.state.questions to the structure the editors expect
  const normalizeIncomingQuestions = (incoming = []) => {
    return incoming.map((q, idx) => {
      const mappedType = toEditorType(q.type);
      // keep existing keys (questionText, choices, isMandatory, maxMarks, icon, id)
      // but ensure an id exists and type is normalized
      return {
        id: q.id || `${Date.now()}-${idx}`,
        type: mappedType,
        icon: q.icon,
        isMandatory: q.isMandatory ?? q.is_mandatory ?? false,
        maxMarks: q.maxMarks ?? q.max_marks ?? q.marks ?? 1,
        questionText: q.questionText ?? q.text ?? q.question ?? "",
        // keep choices as-is (choiceText, id, isCorrect) to allow editors to read them directly
        choices: Array.isArray(q.choices)
          ? q.choices.map((c, cidx) => ({
              id: c.id || `${q.id || idx}-choice-${cidx}`,
              choiceText: c.choiceText ?? c.choice_text ?? c.text ?? c.label ?? "",
              isCorrect: !!(c.isCorrect ?? c.is_correct),
              // keep other fields if present (e.g., image urls)
              ...c,
            }))
          : Array.isArray(q.choice)
          ? q.choice.map((c, cidx) => ({
              id: c.id || `${q.id || idx}-choice-${cidx}`,
              choiceText: c.choiceText ?? c.choice_text ?? c.text ?? c.label ?? "",
              isCorrect: !!(c.isCorrect ?? c.is_correct),
              ...c,
            }))
          : [],
        // carry through any other fields
        ...q,
      };
    });
  };

  // Convert editor question -> GraphQL QuestionInput shape expected by backend
  const toGraphQLQuestion = (q) => {
    const rawChoices = Array.isArray(q.choices)
      ? q.choices
      : Array.isArray(q.choice)
      ? q.choice
      : [];

    const mappedChoices = rawChoices.map((c) => {
      const choiceText = c.choiceText ?? c.choice_text ?? c.text ?? c.label ?? "";
      const isCorrect = !!(c.isCorrect ?? c.is_correct);

      // <-- PREFERRED: use the actual File object if editor provides it (c._file).
      // If no File, fall back to any hosted URL or preview string.
      const img = c._file ?? c.choiceImage?.url ?? c.img ?? c.imageUrl ?? null;

      const correctChoice = c.correctChoice ?? c.correct_choice ?? null;

      // normalize correct_list -> correct_choice if present
      let normalizedCorrectChoice = correctChoice;
      if (!normalizedCorrectChoice && Array.isArray(c.correct_list) && c.correct_list.length) {
        normalizedCorrectChoice = c.correct_list[0]?.correct_choice ?? normalizedCorrectChoice;
      }

      // IMPORTANT: do NOT include `id` property on outgoing choices (backend ChoiceInput doesn't accept it)
      const out = {
        choice_text: choiceText,
        is_correct: isCorrect,
        ...(img ? { img } : {}),
        ...(normalizedCorrectChoice !== null && normalizedCorrectChoice !== undefined
          ? { correct_choice: normalizedCorrectChoice }
          : {}),
      };

      return out;
    });

    const questionType =
      backendTypeMap[q.type] ?? backendTypeMap[toEditorType(q.type)] ?? q.type ?? "mcq";

    const marks = Number(q.maxMarks ?? q.max_marks ?? q.marks ?? 0);

    return {
      text: q.questionText ?? q.text ?? q.question ?? "",
      marks: Number.isNaN(marks) ? 0.0 : parseFloat(marks),
      is_mandatory: !!(q.isMandatory ?? q.is_mandatory ?? false),
      question_type: questionType,
      ...(mappedChoices.length ? { choice: mappedChoices } : {}),
    };
  };


  // Initialize title/instructions from location.state
  useEffect(() => {
    if (location?.state) {
      setQuizData((prevData) => ({
        ...prevData,
        title: location.state.title || "",
        instructions: location.state.instructions || "",
      }));
    }
  }, [location]);

  // Initialize questions from location.state (only once when incoming questions exist)
  useEffect(() => {
    if (location?.state?.questions && location.state.questions.length > 0) {
      // Avoid re-initializing if activeQuestions already set (prevents duplicate editors)
      if (activeQuestions.length === 0) {
        const normalized = normalizeIncomingQuestions(location.state.questions);
        setActiveQuestions(normalized);
        // set quizData.questions to normalized (editor-friendly) — we'll convert to backend shape when saving
        setQuizData((prev) => ({ ...prev, questions: normalized }));
        // make sure refs array has the right length
        refs.current = new Array(normalized.length);
      }
    }
  }, [location?.state?.questions]);

  // Keep refs array in sync if activeQuestions changes (e.g., user adds/removes)
  useEffect(() => {
    refs.current = refs.current.slice(0, activeQuestions.length);
  }, [activeQuestions.length]);

  const handleAddQuestion = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleSelectQuestionType = (type) => {
    const newQuestion = {
      id: Date.now(), // Unique ID for React rendering
      type: toEditorType(type),
      initialData: { type: toEditorType(type) },
      // minimal empty payload editors will fill
      questionText: "",
      choices: [],
      isMandatory: false,
      maxMarks: 1,
    };
    setActiveQuestions((prev) => [...prev, newQuestion]);
    setIsModalOpen(false);
  };

  // Save for a single editor (called when an editor's own save is used)
  const handleSaveQuestion = async (id, data) => {
    setLoading(true);
    // `data` should be the question payload returned by the child editor (internal shape)
    const { errorMessage, ...questionData } = data || {};

    // Update the corresponding activeQuestion entry (find by id)
    const updatedActive = activeQuestions.map((q) => (q.id === id ? { ...q, ...questionData } : q));
    setActiveQuestions(updatedActive);

    // Build the backend-ready questions list by replacing existing question with updated one (dedupe by id)
    const existing = Array.isArray(quizData.questions) ? quizData.questions : [];
    const merged = [...existing];
    const idx = merged.findIndex((q) => q.id === id);

    // Normalize saved question's type to editor type so UI remains consistent
    const normalizedQuestionData = {
      ...questionData,
      type: toEditorType(questionData?.type ?? (merged[idx] && merged[idx].type) ?? "multipleChoice"),
      id,
    };

    if (idx >= 0) merged[idx] = { ...merged[idx], ...normalizedQuestionData };
    else merged.push(normalizedQuestionData);

    // convert to backend shape
    const backendQuestions = merged.map(toGraphQLQuestion);

    const quizPayload = {
      title: quizData.title,
      details: quizData.instructions,
      questions: backendQuestions,
    };

    try {
      console.log("FILES FOUND IN PAYLOAD:", findFiles({ input: { questions: updatedQuestions } }));      
      await dispatch(createTemplate(quizPayload)).then(() => {
        // after single-question save, push to quiz/create route (keeps your existing UX)
        history.push(`/quiz/create/${id}`);
      });
      // update quizData.questions to the merged editor-friendly state
      setQuizData((prev) => ({ ...prev, questions: merged }));
    } catch (error) {
      handleErrorMessage(error);
    } finally {
      setLoading(false);
    }
  };

  // Bulk save — when parent calls save on all editor refs
  const onSave = async (data /* array of editor-returned objects */) => {
    setLoading(true);

    // `data` is expected to be the array of question payloads returned by child editors (internal/editor shape)
    // Instead of appending to existing quizData.questions (which causes duplicates),
    // we will replace / dedupe by id so there's only one entry per question id.
    try {
      // Build a map by id where editor-returned values overwrite incoming ones
      const mapById = {};

      // Start with existing quizData.questions (editor-friendly)
      if (Array.isArray(quizData.questions)) {
        quizData.questions.forEach((q) => {
          mapById[q.id] = { ...(q || {}), type: toEditorType(q.type) };
        });
      }

      // Merge editor returned data (these should be authoritative)
      if (Array.isArray(data)) {
        data.forEach((q) => {
          // q may not have id — generate a stable one if missing
          const qid = q.id ?? `q-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
          mapById[qid] = { ...(mapById[qid] || {}), ...q, id: qid, type: toEditorType(q.type) };
        });
      }

      // Final editor-friendly questions array (no duplicates, editor data takes precedence)
      const updatedQuestions = Object.values(mapById);

      // Convert to backend shape
      const backendQuestions = updatedQuestions.map(toGraphQLQuestion);

      const quizPayload = {
        title: quizData.title,
        details: quizData.instructions,
        questions: backendQuestions,
      };

      await dispatch(createTemplate(quizPayload)).then(() => {
        history.push(`/quiz/create/${id}`, {
          sectionId: location?.state?.sectionId,
        });
      });

      // Update local state to the merged editor-friendly questions (so UI and subsequent saves remain consistent)
      setQuizData((prev) => ({ ...prev, questions: updatedQuestions }));
    } catch (error) {
      handleErrorMessage(error);
    } finally {
      setLoading(false);
      setActiveQuestions([]); // keep your old behavior
    }
  };

  const handleCancelQuestion = () => {
    setIsEditingQuestion(false);
    setCurrentQuestionType(null);
    setEditingIndex(null);
  };

  const handleSaveQuiz = async () => {
    setLoading(true);
    try {
      // gather save() results — support synchronous or Promise-returning save
      const saveFns = refs.current
        .map((ref) => (ref && typeof ref.save === "function" ? ref.save() : null))
        .filter(Boolean);

      // wrap values into promises so Promise.all works for both sync & async
      const savePromises = saveFns.map((v) => Promise.resolve(v));
      const resolved = await Promise.all(savePromises);

      // filter out null/undefined
      const allPayloads = resolved.filter(Boolean);

      if (allPayloads.length === 0) {
        if (quizData.questions && quizData.questions.length > 0) {
          // fallback to existing quizData.questions
          await onSave(quizData.questions);
        } else {
          dispatch(handleErrorMessage("Quiz template must have at least one question!"));
        }
        return;
      }

      // pass collected payloads to onSave (which does backend conversion + dispatch)
      await onSave(allPayloads);
    } catch (e) {
      console.error("Save quiz failed:", e);
      dispatch(handleErrorMessage("Save failed. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    history.push(`/template/create/${id}`);
  };

  const handleMoveUP = (index) => {
    if (index === 0) return;
    const newArray = [...activeQuestions];
    const temp = newArray[index - 1];
    newArray[index - 1] = newArray[index];
    newArray[index] = temp;
    setActiveQuestions(newArray);
  };

  const handleMoveDown = (index) => {
    if (index === activeQuestions.length - 1) return;
    const newArray = [...activeQuestions];
    const temp = newArray[index + 1];
    newArray[index + 1] = newArray[index];
    newArray[index] = temp;
    setActiveQuestions(newArray);
  };

  const handleDeleteQuizQuestionType = (indexOrId) => {
    // allow passing either index or id
    const newArray = activeQuestions.filter(
      (i, idx) => i.id !== indexOrId && idx !== indexOrId
    );
    setActiveQuestions(newArray);
  };

  const renderEditorByType = (q, index) => {
    // normalize type defensively for rendering
    const editorType = toEditorType(q.type);

    const commonProps = {
      initialData: q,
      normalizedType: editorType,
      onSave: (data) => handleSaveQuestion(q.id, data),
      onCancel: () => handleCancelQuestion(q.id),
      onMoveUp: () => handleMoveUP(index),
      onMoveDonw: () => handleMoveDown(index),
      onDeleteQuestion: () => handleDeleteQuizQuestionType(q.id),
    };

    switch (editorType) {
      case "multipleChoice":
        return (
          <MultipleChoiceQuestion
            {...commonProps}
            ref={(el) => (refs.current[index] = el)}
          />
        );
      case "fillBlanks":
        return (
          <FillInTheBlankQuestion
            {...commonProps}
            ref={(el) => (refs.current[index] = el)}
          />
        );
      case "checkboxes":
        return (
          <CheckboxesQuestion
            {...commonProps}
            ref={(el) => (refs.current[index] = el)}
          />
        );
      case "trueFalse":
        return (
          <TrueFalseQuestion
            {...commonProps}
            ref={(el) => (refs.current[index] = el)}
          />
        );
      case "grid":
        return (
          <GridQuestionComponent
            {...commonProps}
            ref={(el) => (refs.current[index] = el)}
          />
        );
      case "imageChoice":
        return (
          <ImageQuestionComponent
            {...commonProps}
            ref={(el) => (refs.current[index] = el)}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={handleGoBack}>
          <ArrowLeftIcon />
          <span>{t("courseMonetisation.goBackBtn")}</span>
        </button>
        <div className={styles.titleContainer}>
          <div className={styles.iconContainer}>
            <QuizIcon sx={{ color: "#fff" }} />
          </div>
          <h1 className={styles.mainTitle}>{quizData.title}</h1>
        </div>

        <Button
          type="solid"
          disable={loading}
          label={
            loading ? (
              <CircularProgress color="inherit" size={20} />
            ) : (
              t("quiz.save")
            )
          }
          clickHandler={handleSaveQuiz}
          className={styles.saveButton}
        />
      </div>

      <div className={styles.questionWrapper}>
        {activeQuestions.map((q, index) => (
          <div key={q.id} className="question-editor">
            {renderEditorByType(q, index)}
          </div>
        ))}

        <div className={styles.content} onClick={handleAddQuestion}>
          <div className={styles.innerContent}>
            <AddIcon sx={{ color: "#F33070" }} />
            <span>{t("quiz.addQuestion")}</span>
          </div>
        </div>
      </div>

      <QuestionTypeModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSelectQuestionType={handleSelectQuestionType}
      />
    </div>
  );
};

export default TestQuiz;